"use client";

import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Button,
} from "@mui/material";
import { getCookie } from "cookies-next";
import authenticationApi from "@/lib/authenticationApi";
import WarningModal from "@/components/WarningModal";
import {
  CompanyAssociation,
  fetchCompanyAssociations,
} from "@/lib/companyAssociation";

interface LoginCompanySelectionModalProps {
  open: boolean;
  onSelect: (company: CompanyAssociation) => void;
  onCancel: () => void;
}

const LoginCompanySelectionModal: React.FC<
  LoginCompanySelectionModalProps
> = ({ open, onSelect, onCancel }) => {
  const [companies, setCompanies] = useState<CompanyAssociation[]>([]);
  const [selectedCompanyUserId, setSelectedCompanyUserId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setCompanies([]);
      setSelectedCompanyUserId("");
      setError("");
      return;
    }

    const fetchCompanies = async () => {
      setLoading(true);
      setError("");

      try {
        const list = await fetchCompanyAssociations(authenticationApi, {
          p_ca_user_id: getCookie("ca_user_id"),
          p_company_id: getCookie("company_id"),
          p_logged_in_company_user_id: getCookie("company_user_id"),
        });

        setCompanies(list);

        if (list.length === 0) {
          setError("No company accounts found. Please contact your administrator.");
        }
      } catch (err: any) {
        setError(
          err.details?.error_message ||
            err.message ||
            "Failed to load companies. Please try again.",
        );
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [open]);

  const handleContinue = () => {
    const selected = companies.find(
      (company) => String(company.company_user_id) === selectedCompanyUserId,
    );

    if (!selected) {
      setError("Please select a company.");
      return;
    }

    onSelect(selected);
  };

  const handleCancelConfirm = () => {
    setIsCancelConfirmOpen(false);
    onCancel();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "10px" },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            color: "#4D4D4D",
            pb: 1,
          }}
        >
          SELECT COMPANY
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              color: "#767C88",
              fontSize: "16px",
              mb: 2,
            }}
          >
            Please select a company account to continue.
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#153C7F" }} />
            </Box>
          ) : (
            <>
              {error && (
                <Typography
                  sx={{
                    color: "#F44336",
                    fontSize: "14px",
                    mb: 2,
                  }}
                >
                  {error}
                </Typography>
              )}

              <RadioGroup
                value={selectedCompanyUserId}
                onChange={(event) => {
                  setSelectedCompanyUserId(event.target.value);
                  if (error === "Please select a company.") {
                    setError("");
                  }
                }}
              >
                {companies.map((company) => {
                  const isSelected =
                    String(company.company_user_id) === selectedCompanyUserId;

                  return (
                    <Box
                      key={`${company.company_id}-${company.company_user_id}`}
                      sx={{
                        border: isSelected
                          ? "2px solid #153C7F"
                          : "1px solid #E5E7EB",
                        borderRadius: "8px",
                        mb: 1.5,
                        px: 2,
                        py: 1.5,
                        backgroundColor: isSelected ? "#F3F1FF" : "#fff",
                      }}
                    >
                      <FormControlLabel
                        value={String(company.company_user_id)}
                        control={
                          <Radio
                            sx={{
                              color: "#767C88",
                              "&.Mui-checked": { color: "#153C7F" },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#4D4D4D",
                              }}
                            >
                              {company.company_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#767C88",
                              }}
                            >
                              {company.role_name}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: "100%" }}
                      />
                    </Box>
                  );
                })}
              </RadioGroup>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2, justifyContent: "flex-end" }}>
          <Button
            type="button"
            onClick={() => setIsCancelConfirmOpen(true)}
            disabled={loading}
            sx={{
              border: "1px solid #F3F1FF",
              borderRadius: "6px",
              backgroundColor: "#F3F1FF",
              color: "#767C88",
              fontWeight: 400,
              fontSize: "16px",
              textTransform: "none",
              px: 3,
              py: 1.25,
              "&:hover": {
                backgroundColor: "#E8E5FF",
                borderColor: "#E8E5FF",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={loading || companies.length === 0}
            sx={{
              borderRadius: "6px",
              backgroundColor: "#63A821",
              color: "#fff",
              fontWeight: 600,
              fontSize: "16px",
              textTransform: "none",
              px: 3,
              py: 1.25,
              "&:hover": {
                backgroundColor: "#4e8c1a",
              },
              "&.Mui-disabled": {
                backgroundColor: "#63A821",
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            CONTINUE
          </Button>
        </DialogActions>
      </Dialog>

      <WarningModal
        open={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
        type="warning"
        title="CANCEL COMPANY SELECTION"
        message="Are you sure you would like to cancel and return to Login?"
        primaryButtonText="YES"
        secondaryButtonText="NO"
        onPrimary={handleCancelConfirm}
        onSecondary={() => setIsCancelConfirmOpen(false)}
      />
    </>
  );
};

export default LoginCompanySelectionModal;
